import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Plus, X, Loader2, Headphones, Search, AlertCircle,
} from 'lucide-react';
import { api } from '@/utils/api';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { formatDate } from '@/utils/formatters';
import { useAuthStore } from '@/stores/auth';
import {
  isSupportDeskRole, moduleLabel, SUPPORT_MODULES, SOURCE_LABELS,
} from './supportConstants';

// Support tickets — standalone flat-FSM model. NO SOP. Separate table
// from ServiceTicket (see spec §1.2).
//
// L229-WEB-3: this page is the SUPPORT DESK — desk-roles-only. Everyone
// else is redirected to the reporter page /support/my. UI-honesty gate;
// the backend independently scopes non-desk roles to own-only.

interface SupportTicket {
  id: string;
  number: string;
  subject: string;
  body?: string;
  status: 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  channel: 'web' | 'whatsapp' | 'email' | 'phone';
  // L229 additive fields
  category?: 'bug' | 'feature_request' | null;
  module?: string | null;
  source?: string | null;
  appVersion?: string | null;
  createdById?: string | null;
  createdBy?: { firstName: string; lastName: string } | null;
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  assignedTo?: { firstName: string; lastName: string } | null;
  createdAt: string;
  slaDeadline?: string | null;
}

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  in_progress: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300',
  waiting_customer: 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
  resolved: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  closed: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const PRIORITY_STYLES: Record<string, string> = {
  low: 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  medium: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  high: 'bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
  critical: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
};

export function SupportTicketsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [moduleFilter, setModuleFilter] = useState<string>('');

  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    subject: '',
    body: '',
    priority: 'medium',
    channel: 'web' as SupportTicket['channel'],
    customerName: '',
    customerPhone: '',
    customerEmail: '',
  });
  const [formErr, setFormErr] = useState<string | null>(null);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // category/module are SERVER-side filters (desk-only, L229-BE) — the
      // rest of the filtering stays client-side as before.
      const params = new URLSearchParams();
      if (categoryFilter) params.set('category', categoryFilter);
      if (moduleFilter) params.set('module', moduleFilter);
      const qs = params.toString();
      const res = await api.get(`/support/tickets${qs ? `?${qs}` : ''}`);
      const data = res.data.data?.tickets ?? res.data.data ?? [];
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to load support tickets';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, moduleFilter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleCreate = async () => {
    if (!form.subject.trim()) {
      setFormErr('Subject is required.');
      return;
    }
    setSaving(true);
    setFormErr(null);
    try {
      const res = await api.post('/support/tickets', {
        subject: form.subject,
        body: form.body || undefined,
        priority: form.priority,
        channel: form.channel,
        customerName: form.customerName || undefined,
        customerPhone: form.customerPhone || undefined,
        customerEmail: form.customerEmail || undefined,
      });
      const created = res.data.data?.ticket ?? res.data.data ?? null;
      setShowCreate(false);
      setForm({ subject: '', body: '', priority: 'medium', channel: 'web', customerName: '', customerPhone: '', customerEmail: '' });
      if (created?.id) navigate(`/support/tickets/${created.id}`);
      else fetchTickets();
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to create ticket';
      setFormErr(msg);
    } finally {
      setSaving(false);
    }
  };

  const filtered = tickets.filter(t => {
    if (statusFilter && t.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !t.subject.toLowerCase().includes(q) &&
        !t.number.toLowerCase().includes(q) &&
        !(t.customerName || '').toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  // L229-WEB-3 desk gate — while the auth store hydrates render nothing
  // (no flash, no fetch side effects on top of the redirect).
  if (!user) return null;
  if (!isSupportDeskRole(user.role?.name)) {
    return <Navigate to="/support/my" replace />;
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">System Support</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Bug reports and feature requests raised by your team. (Customer service requests live in the Service desk.)</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} />
          New ticket
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search subject, number or reporter"
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="waiting_customer">Waiting customer</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        {/* L229 — category + module server-side filters */}
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">All categories</option>
          <option value="bug">Bug</option>
          <option value="feature_request">Feature request</option>
        </select>
        <select
          value={moduleFilter}
          onChange={e => setModuleFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">All modules</option>
          {SUPPORT_MODULES.map(m => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={22} className="animate-spin text-[var(--saffron-600)]" />
        </div>
      ) : error ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle size={18} /> {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 py-16 text-gray-500 dark:border-gray-700 dark:bg-gray-900">
          <Headphones size={32} className="mb-2 text-gray-300" />
          <div className="text-sm">No support tickets</div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                <th className="px-4 py-2.5">Number</th>
                <th className="px-4 py-2.5">Subject</th>
                <th className="px-4 py-2.5">Category</th>
                <th className="px-4 py-2.5">Module</th>
                <th className="px-4 py-2.5">Reporter</th>
                <th className="px-4 py-2.5">Priority</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Source</th>
                <th className="px-4 py-2.5">Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr
                  key={t.id}
                  onClick={() => navigate(`/support/tickets/${t.id}`)}
                  className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800"
                >
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{t.number}</td>
                  <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">{t.subject}</td>
                  <td className="px-4 py-2.5">
                    {t.category ? (
                      <span className="inline-flex rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-950 dark:text-purple-300">
                        {t.category === 'feature_request' ? 'Feature request' : 'Bug'}
                      </span>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-400">{moduleLabel(t.module)}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-700 dark:text-gray-300">
                    {t.createdBy
                      ? `${t.createdBy.firstName} ${t.createdBy.lastName}`.trim()
                      : (t.customerName || '—')}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.medium}`}>
                      {t.priority}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[t.status] || STATUS_STYLES.open}`}>
                      {t.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">
                    {t.source ? (SOURCE_LABELS[t.source] ?? t.source) : <span className="capitalize">{t.channel}</span>}
                    {t.appVersion ? <span className="text-gray-400"> · v{t.appVersion}</span> : null}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(t.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <Modal open onClose={() => setShowCreate(false)} className="!bg-white p-5 dark:!bg-gray-900">
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                New ticket
              </h2>
              <RequestCloseButton
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={18} />
              </RequestCloseButton>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Subject *
                </label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={form.body}
                  onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Priority</label>
                  <select
                    value={form.priority}
                    onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Channel</label>
                  <select
                    value={form.channel}
                    onChange={e => setForm(f => ({ ...f, channel: e.target.value as SupportTicket['channel'] }))}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="web">Web</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                  <input
                    type="text"
                    value={form.customerName}
                    onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
                  <input
                    type="tel"
                    value={form.customerPhone}
                    onChange={e => setForm(f => ({ ...f, customerPhone: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                  <input
                    type="email"
                    value={form.customerEmail}
                    onChange={e => setForm(f => ({ ...f, customerEmail: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
              </div>
              {formErr && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  <AlertCircle size={14} /> {formErr}
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <RequestCloseButton
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancel
              </RequestCloseButton>
              <button
                onClick={handleCreate}
                disabled={saving || !form.subject.trim()}
                className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[var(--ink-1)] disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
